import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Badge,
  Select,
  Card,
  CardBody,
  useBreakpointValue,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { FiTrash2, FiEye } from 'react-icons/fi'
import { getAdminCommunityList, deleteAdminCommunity } from '../../services/apiService'
import dayjs from 'dayjs'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

interface Community {
  id: number
  userId: number
  username: string
  title: string
  content: string
  viewCount: number
  likeCount: number
  createdAt: string
  updatedAt: string | null
}

const Community = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [communities, setCommunities] = useState<Community[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)

  const fetchCommunities = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAdminCommunityList(page, limit)
      if (res.statusCode === 200) {
        setCommunities(res.data.communities)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('게시글 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCommunities()
  }, [page, limit])

  const handleDeleteClick = (id: number) => {
    setDeletingId(id)
    onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!deletingId) return

    try {
      const { data: res } = await deleteAdminCommunity(deletingId)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        setDeletingId(null)
        fetchCommunities()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <DefaultLayout>
      <AdminNav items={adminNavItems} />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">커뮤니티 관리</Heading>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    총 {total}개의 게시글
                  </Text>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                    width="120px"
                    size="sm"
                  >
                    <option value={10}>10개씩</option>
                    <option value={20}>20개씩</option>
                    <option value={50}>50개씩</option>
                  </Select>
                </HStack>

                {isMobile ? (
                  <VStack spacing={4} align="stretch">
                    {communities.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 게시글이 없습니다.</Text>
                      </Box>
                    ) : (
                      communities.map((community) => (
                        <Card key={community.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                                  {community.title}
                                </Text>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/community/${community.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(community.id)}
                                  />
                                </HStack>
                              </HStack>
                              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                {community.content}
                              </Text>
                              <HStack justify="space-between" fontSize="xs" color="gray.500">
                                <Text>{community.username}</Text>
                                <HStack spacing={2}>
                                  <Text>조회 {community.viewCount}</Text>
                                  <Text>좋아요 {community.likeCount}</Text>
                                  <Text>{dayjs(community.createdAt).format('YYYY-MM-DD')}</Text>
                                </HStack>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>ID</Th>
                          <Th>제목</Th>
                          <Th>작성자</Th>
                          <Th>조회수</Th>
                          <Th>좋아요</Th>
                          <Th>작성일시</Th>
                          <Th>관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {communities.length === 0 ? (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 게시글이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          communities.map((community) => (
                            <Tr key={community.id}>
                              <Td>{community.id}</Td>
                              <Td>
                                <Text
                                  fontWeight="medium"
                                  cursor="pointer"
                                  _hover={{ color: 'brand.600' }}
                                  onClick={() => navigate(`/community/${community.id}`)}
                                  noOfLines={1}
                                  maxW="300px"
                                >
                                  {community.title}
                                </Text>
                              </Td>
                              <Td>{community.username}</Td>
                              <Td>{community.viewCount}</Td>
                              <Td>{community.likeCount}</Td>
                              <Td>
                                {dayjs(community.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/community/${community.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(community.id)}
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {totalPages > 0 && (
                  <HStack justify="center" spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      isDisabled={page === 1}
                    >
                      {t('이전')}
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      {t('다음')}
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default Community

